/**
 *
 * ControlPanel
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, ListGroup, Panel } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { lightBlack, green500, green200, blue500, white } from 'styles/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  PrivacyLink,
  SettingsLink,
  SupportLink,
  SocialMediaLinks,
  PremiumLink,
} from 'components/Footer';
import RouteControls from './RouteControls';
import ClubControls from './ClubControls';
import messages from './messages';
import { PANEL_WIDTH, PANEL_WIDTH_LG, LG_MIN } from './constants';
import useControlPanel from './useControlPanel';

const Wrapper = styled.div`
  z-index: 1035;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: row-reverse;
  align-items: flex-start;
  .panel {
    height: 100%;
    max-height: 100%;
    border-radius: 0;
    width: ${PANEL_WIDTH}px;
    display: flex;
    margin-bottom: 0;
    flex-direction: column;
    transition: margin-left 200ms;
    overflow-y: auto;
    .panel-body {
      & > * {
        margin-bottom: 15px;
      }
    }

    .panel-body > a {
      display: flex;
      align-items: center;
      svg {
        margin-right: 10px;
        font-size: 36px;
        color: ${blue500};
      }
    }
    .panel-heading:not(:first-child),
    & > .list-group {
      border-top: 2px solid ${green200};
    }
    .panel-title {
      display: flex;
      & > svg {
        color: ${green500};
        margin-right: 5px;
      }
      & > span {
        flex-grow: 1;
      }
    }
    &.panel-hidden {
      margin-left: -${PANEL_WIDTH}px;
    }
  }
  @media (min-width: ${LG_MIN}px) {
    .panel {
      width: ${PANEL_WIDTH_LG}px;
      &.panel-hidden {
        margin-left: -${PANEL_WIDTH_LG}px;
      }
    }
  }
  .panel-footer {
    background-color: ${white};
    flex-grow: 1;
    flex-wrap: wrap;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-bottom: 10px;
  }
  @media print {
    display: none;
  }
`;

const Toggle = styled(Button)`
  &.btn {
    margin-top: 10px;
    padding: 14px 0;
    margin-left: -4px;
    color: ${lightBlack};
  }
`;

function ControlPanel(props) {
  const { editing } = props;
  const {
    showControl,
    toggleShowControl,
    resetShowControl,
  } = useControlPanel();
  return (
    <Wrapper>
      <Toggle onClick={toggleShowControl} aria-label="Toggle control panel">
        <FontAwesomeIcon
          icon={showControl ? 'caret-left' : 'caret-right'}
          size="lg"
          fixedWidth
        />
      </Toggle>
      <Panel className={showControl ? 'panel-visible' : 'panel-hidden'}>
        <Panel.Heading>
          <Panel.Title>
            <FontAwesomeIcon icon="route" size="lg" fixedWidth />
            <FormattedMessage {...messages.routes} />
          </Panel.Title>
        </Panel.Heading>
        <Panel.Body className="pb-0">
          <RouteControls editing={editing} onChange={resetShowControl} />
        </Panel.Body>
        <Panel.Heading>
          <Panel.Title>
            <FontAwesomeIcon icon="users" size="lg" fixedWidth />
            <FormattedMessage {...messages.clubs} />
          </Panel.Title>
        </Panel.Heading>
        <Panel.Body>
          <ClubControls />
        </Panel.Body>
        <ListGroup>
          <SupportLink className="panel-title list-group-item" />
          <SettingsLink className="panel-title list-group-item" />
          <PremiumLink className="panel-title list-group-item" />
        </ListGroup>
        <Panel.Footer className="mb-0 pb-1">
          <div className="d-flex justify-content-between align-items-center">
            <PrivacyLink />
            <SocialMediaLinks />
          </div>
        </Panel.Footer>
      </Panel>
    </Wrapper>
  );
}

ControlPanel.propTypes = {
  editing: PropTypes.number,
};

export default ControlPanel;
